import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAppContext } from '../../context/AppContext';
import {
  getAdminPassTypes,
  createAdminPassType,
  updateAdminPassType,
  deleteAdminPassType,
  getAdminClassTypes,
} from '../../services/adminService';
import { PASS_CARD_THEMES, DEFAULT_PASS_CARD_THEME, getPassCardTheme } from '../../utils/passCardThemes';

const BADGE_STYLES = {
  gray: 'bg-gray-100 text-gray-600',
  amber: 'bg-amber-100 text-amber-700',
  blue: 'bg-blue-100 text-blue-700',
  purple: 'bg-purple-100 text-purple-700',
  green: 'bg-green-100 text-green-700',
  pink: 'bg-pink-100 text-pink-700',
};

function Badge({ color = 'gray', children }) {
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${BADGE_STYLES[color] || BADGE_STYLES.gray}`}>
      {children}
    </span>
  );
}

function MatrixToggle({ checked, onClick, loading }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onClick}
      disabled={loading}
      className={`inline-flex items-center justify-center h-6 w-6 rounded-full border transition-all duration-150 ${
        checked
          ? 'bg-brown border-brown text-white shadow-sm'
          : 'bg-white border-gray-300 text-transparent hover:border-brown/50'
      } ${loading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
    >
      {loading ? (
        <span className="h-2.5 w-2.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
      ) : (
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
          <path
            fillRule="evenodd"
            d="M16.704 5.29a1 1 0 010 1.415l-7.5 7.5a1 1 0 01-1.415 0l-3.5-3.5a1 1 0 111.415-1.415L8.5 12.086l6.79-6.79a1 1 0 011.415 0z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </button>
  );
}

function SuggestionChips({ options, value, onPick }) {
  if (!options.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {options.map((label) => (
        <button
          key={label}
          type="button"
          onClick={() => onPick(label)}
          className={`px-2 py-0.5 rounded-full text-xs border transition ${
            value === label
              ? 'bg-brown/10 border-brown text-brown'
              : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

const EMPTY_FORM = {
  name: '',
  displayName: '',
  displayNameRu: '',
  classesIncluded: '',
  daysValid: '',
  basePrice: '',
  currency: 'ILS',
  eurDisplayPrice: '',
  descriptionEn: '',
  descriptionRu: '',
  sectionLabel: '',
  sectionLabelRu: '',
  showOnSchedule: true,
  active: true,
  trial: false,
  testOnly: false,
  subscriptionEnabled: false,
  subscriptionDiscountPercent: 0,
  subscriptionMonthlyPrice: '',
  subscriptionRequired: false,
  firstPurchaseActive: false,
  firstPurchaseSubjectRu: '',
  firstPurchaseBodyRu: '',
  firstPurchaseSubjectEn: '',
  firstPurchaseBodyEn: '',
  expiringSoonActive: false,
  expiringSoonDaysBefore: 3,
  expiringSoonSubjectRu: '',
  expiringSoonBodyRu: '',
  expiringSoonSubjectEn: '',
  expiringSoonBodyEn: '',
  allowedClassTypes: [],
  onlineOnly: false,
  color: DEFAULT_PASS_CARD_THEME,
};

export default function AdminPassTypes({ token }) {
  const { setToaster, language } = useAppContext();
  const displayNameOf = (pt) => (language === 'russian' ? (pt.displayNameRu || pt.displayName) : pt.displayName);
  const [passTypes, setPassTypes] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchPassTypes = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await getAdminPassTypes(token);
      setPassTypes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching pass types:', error);
      setToaster({ message: 'Failed to load pass types', type: 'error', show: true });
    } finally {
      setLoading(false);
    }
  };

  const fetchClassTypes = async () => {
    if (!token) return;
    try {
      const data = await getAdminClassTypes(token);
      setClassTypes((Array.isArray(data) ? data : []).filter((ct) => ct.active !== false));
    } catch (error) {
      console.error('Error fetching class types:', error);
    }
  };

  useEffect(() => {
    fetchPassTypes();
    fetchClassTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const classTypeIdOf = (v) => String(v?._id || v);

  const existingSectionLabels = Array.from(new Set(passTypes.map((pt) => pt.sectionLabel).filter(Boolean))).sort();
  const existingSectionLabelsRu = Array.from(new Set(passTypes.map((pt) => pt.sectionLabelRu).filter(Boolean))).sort();

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (pt) => {
    setEditingId(pt._id);
    setForm({
      name: pt.name || '',
      displayName: pt.displayName || '',
      displayNameRu: pt.displayNameRu || '',
      classesIncluded: pt.classesIncluded == null ? '' : String(pt.classesIncluded),
      daysValid: String(pt.daysValid ?? ''),
      basePrice: String(pt.basePrice ?? ''),
      currency: pt.currency || 'ILS',
      eurDisplayPrice: pt.eurDisplayPrice == null ? '' : String(pt.eurDisplayPrice),
      descriptionEn: pt.descriptionEn || '',
      descriptionRu: pt.descriptionRu || '',
      sectionLabel: pt.sectionLabel || '',
      sectionLabelRu: pt.sectionLabelRu || '',
      showOnSchedule: pt.showOnSchedule !== false,
      active: pt.active !== false,
      trial: !!pt.trial,
      testOnly: !!pt.testOnly,
      subscriptionEnabled: !!pt.subscription?.enabled,
      subscriptionDiscountPercent: pt.subscription?.discountPercent || 0,
      subscriptionMonthlyPrice: pt.subscription?.monthlyPrice == null ? '' : String(pt.subscription.monthlyPrice),
      subscriptionRequired: !!pt.subscription?.required,
      firstPurchaseActive: !!pt.emailTemplates?.firstPurchase?.active,
      firstPurchaseSubjectRu: pt.emailTemplates?.firstPurchase?.subjectRu || '',
      firstPurchaseBodyRu: pt.emailTemplates?.firstPurchase?.bodyRu || '',
      firstPurchaseSubjectEn: pt.emailTemplates?.firstPurchase?.subjectEn || '',
      firstPurchaseBodyEn: pt.emailTemplates?.firstPurchase?.bodyEn || '',
      expiringSoonActive: !!pt.emailTemplates?.expiringSoon?.active,
      expiringSoonDaysBefore: pt.emailTemplates?.expiringSoon?.daysBefore ?? 3,
      expiringSoonSubjectRu: pt.emailTemplates?.expiringSoon?.subjectRu || '',
      expiringSoonBodyRu: pt.emailTemplates?.expiringSoon?.bodyRu || '',
      expiringSoonSubjectEn: pt.emailTemplates?.expiringSoon?.subjectEn || '',
      expiringSoonBodyEn: pt.emailTemplates?.expiringSoon?.bodyEn || '',
      allowedClassTypes: (pt.allowedClassTypes || []).map(classTypeIdOf),
      onlineOnly: !!pt.onlineOnly,
      color: pt.color || DEFAULT_PASS_CARD_THEME,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  useEffect(() => {
    if (!showForm) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeForm();
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [showForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        displayName: form.displayName.trim(),
        displayNameRu: form.displayNameRu.trim(),
        classesIncluded: form.classesIncluded === '' ? null : Number(form.classesIncluded),
        daysValid: Number(form.daysValid),
        basePrice: Number(form.basePrice),
        currency: form.currency,
        eurDisplayPrice: form.eurDisplayPrice === '' ? null : Number(form.eurDisplayPrice),
        descriptionEn: form.descriptionEn.trim(),
        descriptionRu: form.descriptionRu.trim(),
        sectionLabel: form.sectionLabel.trim(),
        sectionLabelRu: form.sectionLabelRu.trim(),
        showOnSchedule: form.showOnSchedule,
        active: form.active,
        trial: form.trial,
        testOnly: form.testOnly,
        subscription: {
          enabled: form.subscriptionEnabled,
          discountPercent: Number(form.subscriptionDiscountPercent) || 0,
          monthlyPrice: form.subscriptionMonthlyPrice.trim() === '' ? null : Number(form.subscriptionMonthlyPrice),
          required: form.subscriptionRequired,
        },
        emailTemplates: {
          firstPurchase: {
            active: form.firstPurchaseActive,
            subjectRu: form.firstPurchaseSubjectRu.trim(),
            bodyRu: form.firstPurchaseBodyRu.trim(),
            subjectEn: form.firstPurchaseSubjectEn.trim(),
            bodyEn: form.firstPurchaseBodyEn.trim(),
          },
          expiringSoon: {
            active: form.expiringSoonActive,
            daysBefore: Number(form.expiringSoonDaysBefore) || 3,
            subjectRu: form.expiringSoonSubjectRu.trim(),
            bodyRu: form.expiringSoonBodyRu.trim(),
            subjectEn: form.expiringSoonSubjectEn.trim(),
            bodyEn: form.expiringSoonBodyEn.trim(),
          },
        },
        allowedClassTypes: form.allowedClassTypes,
        onlineOnly: form.onlineOnly,
        color: form.color,
      };
      if (editingId) {
        await updateAdminPassType(token, editingId, payload);
        setToaster({ message: 'Pass type updated', type: 'success', show: true });
      } else {
        await createAdminPassType(token, payload);
        setToaster({ message: 'Pass type created', type: 'success', show: true });
      }
      closeForm();
      fetchPassTypes();
    } catch (error) {
      setToaster({ message: error.response?.data?.error || 'Failed to save pass type', type: 'error', show: true });
    } finally {
      setSaving(false);
    }
  };

  const [togglingKey, setTogglingKey] = useState(null);

  const toggleMatrixCell = async (pt, ctId) => {
    const current = pt.allowedClassTypes || [];
    const isOn = current.some((v) => classTypeIdOf(v) === ctId);
    const next = isOn
      ? current.filter((v) => classTypeIdOf(v) !== ctId)
      : [...current, ctId];

    setTogglingKey(`${pt._id}-${ctId}`);
    // Optimistic update so the checkbox responds instantly.
    setPassTypes((prev) => prev.map((p) => (p._id === pt._id ? { ...p, allowedClassTypes: next } : p)));
    try {
      await updateAdminPassType(token, pt._id, { allowedClassTypes: next.map(classTypeIdOf) });
    } catch (error) {
      setPassTypes((prev) => prev.map((p) => (p._id === pt._id ? { ...p, allowedClassTypes: current } : p)));
      setToaster({ message: error.response?.data?.error || 'Failed to update eligibility', type: 'error', show: true });
    } finally {
      setTogglingKey(null);
    }
  };

  const handleDelete = async (pt) => {
    if (!window.confirm(`Delete pass type "${displayNameOf(pt)}"?`)) return;
    setDeletingId(pt._id);
    try {
      await deleteAdminPassType(token, pt._id);
      setToaster({ message: 'Pass type deleted', type: 'success', show: true });
      fetchPassTypes();
    } catch (error) {
      setToaster({ message: error.response?.data?.error || 'Failed to delete pass type', type: 'error', show: true });
    } finally {
      setDeletingId(null);
    }
  };

  const symbol = (c) => (c === 'RUB' ? '₽' : '₪');

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Pass types</h3>
        <button
          type="button"
          onClick={openAdd}
          className="px-3 py-1.5 bg-brown text-white rounded text-sm hover:opacity-90"
        >
          + Add pass type
        </button>
      </div>

      {!loading && passTypes.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">Which class types can each pass book?</p>
            <p className="text-xs text-gray-400 hidden sm:block">Click a cell to toggle eligibility</p>
          </div>
          <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-sm">
            <table className="text-sm border-collapse min-w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="sticky left-0 z-10 bg-gray-50 py-2.5 px-4 font-semibold text-left text-gray-600 uppercase text-xs tracking-wide border-r border-gray-200">
                    Pass type
                  </th>
                  {classTypes.map((ct) => (
                    <th key={ct._id} className="py-2.5 px-3 font-semibold text-center text-gray-600 uppercase text-xs tracking-wide whitespace-nowrap">
                      {ct.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {passTypes.map((pt, idx) => (
                  <tr key={pt._id} className={`group ${idx % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'} hover:bg-brown/[0.04] transition-colors`}>
                    <td className="sticky left-0 z-10 bg-inherit py-2 px-4 font-medium text-gray-800 whitespace-nowrap border-r border-gray-200">
                      {pt.name}
                    </td>
                    {classTypes.map((ct) => (
                      <td key={ct._id} className="py-2 px-3 text-center">
                        <MatrixToggle
                          checked={(pt.allowedClassTypes || []).some((v) => classTypeIdOf(v) === ct._id)}
                          onClick={() => toggleMatrixCell(pt, ct._id)}
                          loading={togglingKey === `${pt._id}-${ct._id}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-center py-6 text-gray-500 text-sm">Loading…</p>
      ) : passTypes.length === 0 ? (
        <p className="text-center py-6 text-gray-500 text-sm">No pass types yet.</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-sm">
          <table className="w-full text-left text-sm border-collapse min-w-[820px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="py-2.5 px-3 font-semibold text-gray-600 uppercase text-xs tracking-wide">Name</th>
                <th className="py-2.5 px-3 font-semibold text-gray-600 uppercase text-xs tracking-wide">Display name</th>
                <th className="py-2.5 px-3 font-semibold text-gray-600 uppercase text-xs tracking-wide text-right">Price</th>
                <th className="py-2.5 px-3 font-semibold text-gray-600 uppercase text-xs tracking-wide text-right">Classes</th>
                <th className="py-2.5 px-3 font-semibold text-gray-600 uppercase text-xs tracking-wide">Subscription</th>
                <th className="py-2.5 px-3 font-semibold text-gray-600 uppercase text-xs tracking-wide">Flags</th>
                <th className="py-2.5 px-3 font-semibold text-gray-600 uppercase text-xs tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {passTypes.map((pt) => {
                const sub = pt.subscription;
                const subLabel = sub?.enabled
                  ? (sub.monthlyPrice
                    ? `${symbol(pt.currency)}${sub.monthlyPrice}/mo`
                    : sub.discountPercent
                      ? `-${sub.discountPercent}%`
                      : 'Enabled') + (sub.required ? ' (only)' : '')
                  : '—';
                return (
                  <tr key={pt._id} className="hover:bg-gray-50">
                    <td className="py-2 px-3 text-gray-500">{pt.name}</td>
                    <td className="py-2 px-3 font-medium text-gray-900">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full mr-1.5 align-middle"
                        style={{ backgroundColor: getPassCardTheme(pt.color).swatch }}
                        title={getPassCardTheme(pt.color).label}
                      />
                      {displayNameOf(pt)}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {symbol(pt.currency)}{pt.basePrice}
                      {pt.eurDisplayPrice != null && <span className="block text-xs text-gray-400">≈ €{pt.eurDisplayPrice}</span>}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-600">
                      {pt.classesIncluded ?? '∞'} / {pt.daysValid}d
                    </td>
                    <td className="py-2 px-3 text-gray-600">{subLabel}</td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-1">
                        <Badge color={pt.active ? 'green' : 'gray'}>{pt.active ? 'Active' : 'Inactive'}</Badge>
                        {!pt.showOnSchedule && <Badge color="gray">Hidden</Badge>}
                        {pt.trial && <Badge color="amber">Trial</Badge>}
                        {pt.onlineOnly && <Badge color="pink">Online Club</Badge>}
                        {pt.testOnly && <Badge color="gray">Test</Badge>}
                      </div>
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      <button type="button" onClick={() => openEdit(pt)} className="text-brown hover:underline mr-2">
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(pt)}
                        disabled={deletingId === pt._id}
                        className="text-red-600 hover:underline disabled:opacity-50"
                      >
                        {deletingId === pt._id ? '…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && createPortal(
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[70]"
          onClick={closeForm}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <h3 className="text-lg font-semibold">{editingId ? 'Edit pass type' : 'Add pass type'}</h3>
              <button
                type="button"
                onClick={closeForm}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="px-6 py-4 overflow-y-auto space-y-5">
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Basics</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Internal name * <span className="text-xs text-gray-400">(e.g. "standard")</span>
                    </label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display name (English) *</label>
                    <input
                      value={form.displayName}
                      onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display name (Russian)</label>
                    <input
                      value={form.displayNameRu}
                      onChange={(e) => setForm((f) => ({ ...f, displayNameRu: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      placeholder="e.g. Однажды на коврике"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Pricing & duration</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Classes included <span className="text-xs text-gray-400">(blank = unlimited)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={form.classesIncluded}
                        onChange={(e) => setForm((f) => ({ ...f, classesIncluded: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Days valid *</label>
                      <input
                        type="number"
                        min="1"
                        value={form.daysValid}
                        onChange={(e) => setForm((f) => ({ ...f, daysValid: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                      <input
                        type="number"
                        min="0"
                        value={form.basePrice}
                        onChange={(e) => setForm((f) => ({ ...f, basePrice: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                      <select
                        value={form.currency}
                        onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      >
                        <option value="ILS">ILS (₪)</option>
                        <option value="RUB">RUB (₽)</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display price, € (approximate)</label>
                    <input
                      type="number"
                      min="0"
                      value={form.eurDisplayPrice}
                      onChange={(e) => setForm((f) => ({ ...f, eurDisplayPrice: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      placeholder="Leave empty to hide"
                    />
                    <span className="block text-xs text-gray-400 mt-1">Shown to customers as "≈ X €, paid as …". Never charged — payment stays in the currency above. For the online club / single class, set it on both the ₪ and ₽ pass types.</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Description</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (English)</label>
                    <input
                      value={form.descriptionEn}
                      onChange={(e) => setForm((f) => ({ ...f, descriptionEn: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      placeholder="e.g. 4-class studio pass (valid one month)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (Russian)</label>
                    <input
                      value={form.descriptionRu}
                      onChange={(e) => setForm((f) => ({ ...f, descriptionRu: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      placeholder="Например: Абонемент на 4 занятия на 30 дней"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Grouping</h4>
                  <p className="text-xs text-gray-400">
                    Group this pass under a shared header on the schedule page's pass list. Leave blank for no section. Pass types sharing the same label are grouped together.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section label (English)</label>
                    <input
                      value={form.sectionLabel}
                      onChange={(e) => setForm((f) => ({ ...f, sectionLabel: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      placeholder="e.g. Kids Classes"
                    />
                    <SuggestionChips
                      options={existingSectionLabels}
                      value={form.sectionLabel}
                      onPick={(label) => setForm((f) => ({ ...f, sectionLabel: label }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section label (Russian)</label>
                    <input
                      value={form.sectionLabelRu}
                      onChange={(e) => setForm((f) => ({ ...f, sectionLabelRu: e.target.value }))}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                    <SuggestionChips
                      options={existingSectionLabelsRu}
                      value={form.sectionLabelRu}
                      onPick={(label) => setForm((f) => ({ ...f, sectionLabelRu: label }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Card color</h4>
                  <p className="text-xs text-gray-400">Used for this pass type's card on the schedule/profile pages.</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(PASS_CARD_THEMES).map(([key, theme]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, color: key }))}
                        title={theme.label}
                        className={`w-7 h-7 rounded-full border flex items-center justify-center transition ${
                          form.color === key
                            ? 'ring-2 ring-offset-1 ring-brown border-brown'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        style={{ backgroundColor: theme.swatch }}
                      />
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Visibility</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.active}
                        onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                      />
                      Active
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.showOnSchedule}
                        onChange={(e) => setForm((f) => ({ ...f, showOnSchedule: e.target.checked }))}
                      />
                      Show on schedule
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.subscriptionEnabled}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          subscriptionEnabled: e.target.checked,
                          subscriptionRequired: e.target.checked ? f.subscriptionRequired : false,
                        }))}
                      />
                      Subscription
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.trial}
                        onChange={(e) => setForm((f) => ({ ...f, trial: e.target.checked }))}
                      />
                      Trial
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={form.testOnly}
                        onChange={(e) => setForm((f) => ({ ...f, testOnly: e.target.checked }))}
                      />
                      Test only
                    </label>
                  </div>
                  {form.trial && (
                    <p className="text-xs text-gray-400">Hidden from a user once they've bought any pass.</p>
                  )}
                  {form.testOnly && (
                    <p className="text-xs text-gray-400">Excluded from revenue reporting and dashboard stats.</p>
                  )}
                  {form.subscriptionEnabled && (
                    <div className="pt-1 space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subscription discount %</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={form.subscriptionDiscountPercent}
                          onChange={(e) => setForm((f) => ({ ...f, subscriptionDiscountPercent: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fixed monthly price <span className="text-xs text-gray-400">(optional — overrides the discount % above)</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={form.subscriptionMonthlyPrice}
                          onChange={(e) => setForm((f) => ({ ...f, subscriptionMonthlyPrice: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          placeholder="Leave blank to use the discount % instead"
                        />
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.subscriptionRequired}
                          onChange={(e) => setForm((f) => ({ ...f, subscriptionRequired: e.target.checked }))}
                        />
                        Subscription only (no one-time purchase allowed)
                      </label>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Eligibility</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowed class types</label>
                    <div className="flex flex-wrap gap-3">
                      {classTypes.map((ct) => (
                        <label key={ct._id} className="flex items-center gap-1.5 text-sm">
                          <input
                            type="checkbox"
                            checked={form.allowedClassTypes.includes(ct._id)}
                            onChange={(e) => setForm((f) => ({
                              ...f,
                              allowedClassTypes: e.target.checked
                                ? [...f.allowedClassTypes, ct._id]
                                : f.allowedClassTypes.filter((t) => t !== ct._id),
                            }))}
                          />
                          {ct.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-start gap-2 text-sm pt-1">
                    <input
                      type="checkbox"
                      checked={form.onlineOnly}
                      onChange={(e) => setForm((f) => ({ ...f, onlineOnly: e.target.checked }))}
                      className="mt-0.5"
                    />
                    <span>
                      This is the Online Club pass
                      <span className="block text-xs text-gray-400">Identity flag — drives the "onlineClub" user role and dashboard stats. Check this for every currency/pricing variant of the Online Club product.</span>
                    </span>
                  </label>
                </div>

                <div className="space-y-2 border-t border-gray-200 pt-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Emails</h4>
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                      <input
                        type="checkbox"
                        checked={form.firstPurchaseActive}
                        onChange={(e) => setForm((f) => ({ ...f, firstPurchaseActive: e.target.checked }))}
                      />
                      First purchase email
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Only sent when the buyer has no previous passes on their doc. Placeholders: {'{{title}}'}, {'{{price}}'}, {'{{currency}}'}
                    </p>
                    <input
                      value={form.firstPurchaseSubjectRu}
                      onChange={(e) => setForm((f) => ({ ...f, firstPurchaseSubjectRu: e.target.value }))}
                      placeholder="Subject (Russian)"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2"
                    />
                    <textarea
                      value={form.firstPurchaseBodyRu}
                      onChange={(e) => setForm((f) => ({ ...f, firstPurchaseBodyRu: e.target.value }))}
                      placeholder="Body (Russian)"
                      rows={4}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-y"
                    />
                    <p className="text-xs text-gray-500 mt-3 mb-2">
                      English version (optional) — used for buyers whose saved language is English. Leave blank to always send the version above.
                    </p>
                    <input
                      value={form.firstPurchaseSubjectEn}
                      onChange={(e) => setForm((f) => ({ ...f, firstPurchaseSubjectEn: e.target.value }))}
                      placeholder="Subject (English)"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2"
                    />
                    <textarea
                      value={form.firstPurchaseBodyEn}
                      onChange={(e) => setForm((f) => ({ ...f, firstPurchaseBodyEn: e.target.value }))}
                      placeholder="Body (English)"
                      rows={4}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-y"
                    />
                  </div>

                  <div className="pt-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                      <input
                        type="checkbox"
                        checked={form.expiringSoonActive}
                        onChange={(e) => setForm((f) => ({ ...f, expiringSoonActive: e.target.checked }))}
                      />
                      Expiring soon reminder
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Sent once, this many days before the pass expires. Not sent for subscriptions. Placeholders: {'{{title}}'}, {'{{date}}'}, {'{{daysLeft}}'}
                    </p>
                    <div className="mb-2">
                      <label className="block text-xs text-gray-500 mb-1">Days before expiry</label>
                      <input
                        type="number"
                        min="0"
                        value={form.expiringSoonDaysBefore}
                        onChange={(e) => setForm((f) => ({ ...f, expiringSoonDaysBefore: e.target.value }))}
                        className="w-24 border border-gray-300 rounded px-3 py-2 text-sm"
                      />
                    </div>
                    <input
                      value={form.expiringSoonSubjectRu}
                      onChange={(e) => setForm((f) => ({ ...f, expiringSoonSubjectRu: e.target.value }))}
                      placeholder="Subject (Russian)"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2"
                    />
                    <textarea
                      value={form.expiringSoonBodyRu}
                      onChange={(e) => setForm((f) => ({ ...f, expiringSoonBodyRu: e.target.value }))}
                      placeholder="Body (Russian)"
                      rows={4}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-y"
                    />
                    <p className="text-xs text-gray-500 mt-3 mb-2">
                      English version (optional) — used for buyers whose saved language is English. Leave blank to always send the version above.
                    </p>
                    <input
                      value={form.expiringSoonSubjectEn}
                      onChange={(e) => setForm((f) => ({ ...f, expiringSoonSubjectEn: e.target.value }))}
                      placeholder="Subject (English)"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-2"
                    />
                    <textarea
                      value={form.expiringSoonBodyEn}
                      onChange={(e) => setForm((f) => ({ ...f, expiringSoonBodyEn: e.target.value }))}
                      placeholder="Body (English)"
                      rows={4}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-y"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2 px-6 py-4 border-t border-gray-200 shrink-0">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-brown text-white rounded text-sm hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editingId ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
